import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RequireWrite, TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { deleteLp, getLp, updateLp, type LpType } from "@/lib/api/lps";

const LP_TYPES: LpType[] = [
  "pension",
  "sovereign",
  "endowment",
  "foundation",
  "insurance",
  "fof",
  "family_office",
  "hnw",
  "corporate",
  "other",
];

function LpsEditInner() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { lpId } = useParams({ strict: false });

  const {
    data: lp,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: ["lp", lpId],
    queryFn: () => {
      if (!lpId) throw new Error("lpId param missing");
      return getLp(lpId);
    },
    enabled: !!lpId,
  });

  const [legalName, setLegalName] = useState("");
  const [shortName, setShortName] = useState("");
  const [lpType, setLpType] = useState<LpType | "">("");
  const [domicile, setDomicile] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!lp) return;
    setLegalName(lp.legal_name);
    setShortName(lp.short_name ?? "");
    setLpType(lp.lp_type ?? "");
    setDomicile(lp.domicile ?? "");
  }, [lp]);

  const update = useMutation({
    mutationFn: (id: string) =>
      updateLp(id, {
        legal_name: legalName,
        short_name: shortName || null,
        lp_type: lpType || null,
        domicile: domicile || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["lps", tenant.id] });
      await qc.invalidateQueries({ queryKey: ["lp", lpId] });
      await navigate({ to: "/t/$slug/lps", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteLp(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["lps", tenant.id] });
      await navigate({ to: "/t/$slug/lps", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!lpId) return;
    setFormError(null);
    update.mutate(lpId);
  }

  function onDelete(): void {
    if (!lpId || !lp) return;
    const ok = window.confirm(
      `Delete ${lp.legal_name}? This also deletes its fund commitments. This cannot be undone.`,
    );
    if (!ok) return;
    setFormError(null);
    del.mutate(lpId);
  }

  if (isLoading) {
    return <p className="text-sm text-neutral-500">Loading LP…</p>;
  }
  if (loadError || !lp) {
    return (
      <div className="space-y-3">
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {loadError ? loadError.message : "LP not found"}
        </div>
        <Link
          to="/t/$slug/lps"
          params={{ slug: tenant.slug }}
          className="inline-block text-xs text-neutral-400 hover:text-neutral-200"
        >
          ← Back to LPs
        </Link>
      </div>
    );
  }

  return (
    <>
      <header className="space-y-1">
        <Link
          to="/t/$slug/lps"
          params={{ slug: tenant.slug }}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          ← Back to LPs
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Edit {lp.legal_name}</h1>
      </header>

      <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
        <Field label="Legal name" required>
          <input
            required
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            className="input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Short name">
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Domicile">
            <input
              value={domicile}
              onChange={(e) => setDomicile(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <Field label="LP type">
          <select
            value={lpType}
            onChange={(e) => setLpType(e.target.value as LpType | "")}
            className="input"
          >
            <option value="">—</option>
            {LP_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>

        {formError ? (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {formError}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
            <Link
              to="/t/$slug/lps"
              params={{ slug: tenant.slug }}
              className="rounded border border-neutral-800 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
            >
              Cancel
            </Link>
          </div>
          <button
            type="button"
            onClick={onDelete}
            disabled={del.isPending}
            className="rounded border border-red-900/60 px-4 py-2 text-sm text-red-300 hover:bg-red-950/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {del.isPending ? "Deleting…" : "Delete LP"}
          </button>
        </div>
      </form>
    </>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}

function Field({ label, required, children }: FieldProps) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-neutral-400">
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </span>
      {children}
    </label>
  );
}

export function LpsEditPage() {
  return (
    <TenantLayout>
      <RequireWrite>
        <LpsEditInner />
      </RequireWrite>
    </TenantLayout>
  );
}
